import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";

const PartDetails = () => {
  const { id } = useParams();
  const { lang } = useLanguage();

  const [part, setPart] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [form, setForm] = useState({
    name: "",
    surname: "",
    mobile: "",
    address: "",
  });

  // ================= TEXT =================
  const t = {
    sq: {
      whatsapp: "Përshëndetje",
      interested: "Jam i interesuar për këtë pjesë",
      part: "Pjesa",
      stock: "Në stok",
      outStock: "S’ka stok",
      reserve: "Rezervo Pjesën",
      name: "Emri",
      surname: "Mbiemri",
      mobile: "Tel",
      address: "Adresa",
      confirm: "Konfirmo",
      close: "Mbyll",
      send: "Duke u dërguar...",
      success: "✔ Rezervimi u dërgua me sukses!",
      error: "❌ Gabim gjatë rezervimit",
      fill: "Plotëso emrin dhe numrin e telefonit!",
      loading: "Duke u ngarkuar...",
    },
    en: {
      whatsapp: "Hello",
      interested: "I am interested in this part",
      part: "Part",
      stock: "In stock",
      outStock: "Out of stock",
      reserve: "Reserve Part",
      name: "Name",
      surname: "Surname",
      mobile: "Phone",
      address: "Address",
      confirm: "Confirm",
      close: "Close",
      send: "Sending...",
      success: "✔ Reservation sent successfully!",
      error: "❌ Error while reserving",
      fill: "Please fill name and phone number!",
      loading: "Loading...",
    },
  };

  const text = t[lang] || t.sq;

  // ================= FETCH =================
  useEffect(() => {
    if (!id) return;

    const fetchPart = async () => {
      try {
        setFetching(true);

        const res = await fetch(`${API_BASE_URL}/api/parts/${id}`);
        const data = await res.json();

        setPart(data);
      } catch (err) {
        console.error(err);
      } finally {
        setFetching(false);
      }
    };

    fetchPart();
  }, [id]);

  // ================= SAFE STOCK =================
  const stock = Number(part?.stock ?? 0);

  // ================= FORM =================
  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // ================= WHATSAPP =================
  const handleWhatsApp = () => {
    if (!part) return;

    const message = `
${text.whatsapp}

${text.interested}

📦 ${text.part}: ${part.name}
🚗 ${part.brand} ${part.model}
📂 ${part.category}
💰 €${Number(part.price || 0).toFixed(2)}

Faleminderit!
`;

    window.open(
      `https://wa.me/355674909765?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  // ================= RESERVE =================
  const handleReserve = async () => {
    if (!part) return;

    if (!form.name || !form.mobile) {
      alert(text.fill);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/orders/reserve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          email: null,
          product: {
            type: "part",
            id: part.id,
            name: part.name,
            brand: part.brand,
            model: part.model,
            category: part.category,
            price: part.price,
          },
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Error");

      alert(text.success);

      setForm({
        name: "",
        surname: "",
        mobile: "",
        address: "",
      });

      setMode(null);
    } catch (err) {
      console.error(err);
      alert(text.error);
    } finally {
      setLoading(false);
    }
  };

  // ================= LOADING =================
  if (fetching) {
    return (
      <div className="p-10 text-center text-gray-500">
        {text.loading}
      </div>
    );
  }

  if (!part) {
    return (
      <div className="p-10 text-center text-red-500">
        Error loading part
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 grid md:grid-cols-2 gap-8">

      {/* IMAGE */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow p-4 flex justify-center"
      >
        <img
          src={
            part.image
              ? `${API_BASE_URL}${part.image}`
              : "https://placehold.co/400x400"
          }
          className="w-full max-w-sm object-contain"
          alt={part.name}
        />
      </motion.div>

      {/* INFO */}
      <div className="flex flex-col justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold mb-2">
            {part.name}
          </h1>

          <p className="text-gray-500 mb-3">
            {part.category} • {part.brand} • {part.model}
          </p>

          <div className="text-2xl font-bold text-green-600">
            €{Number(part.price || 0).toFixed(2)}
          </div>

          {/* STOCK FIXED */}
          <div className="mt-2 font-semibold">
            {stock > 0 ? (
              <span className="text-green-600">
                ✔ {text.stock} ({stock})
              </span>
            ) : (
              <span className="text-red-600">
                ✖ {text.outStock}
              </span>
            )}
          </div>

          <p className="mt-4 text-gray-600">
            {part.description || "No description"}
          </p>
        </div>

        {/* BUTTONS */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleWhatsApp}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl"
          >
            WhatsApp
          </button>

          <button
            onClick={() => setMode("reserve")}
            className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl"
          >
            {text.reserve}
          </button>
        </div>
      </div>

      {/* MODAL */}
      {mode === "reserve" && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setMode(null)}
        >
          <div
            className="bg-white rounded-2xl p-5 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold mb-4">
              {text.reserve}
            </h2>

            <input
              name="name"
              placeholder={text.name}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              name="surname"
              placeholder={text.surname}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              name="mobile"
              placeholder={text.mobile}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <textarea
              name="address"
              placeholder={text.address}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <button
              onClick={handleReserve}
              disabled={loading}
              className="bg-blue-600 text-white w-full py-2 rounded-lg mt-2"
            >
              {loading ? text.send : text.confirm}
            </button>

            <button
              onClick={() => setMode(null)}
              className="text-red-500 w-full mt-2"
            >
              {text.close}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartDetails;